// src/components/MFA/MFADisableModal.tsx
import { FC, useContext, useState, useEffect, useRef } from "react";
import { mfaService } from '../api';
import Modal from 'shared/ui/Modal';
import Button, { Button_Style, Button_Type } from 'shared/ui/Button/Button';
import './MFADisableModal.scss';
import CodeInput from 'features/mfa/components/CodeInput/CodeInput';
import { UserContext } from "features/account";

interface MFADisableModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  mfaType: string;
}

const MFADisableModal: FC<MFADisableModalProps> = ({ isOpen, onClose, onSuccess, mfaType }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const { user, userType } = useContext(UserContext);
  const hasSentCode = useRef(false);
  const type = user?.type ?? 'individual';


  // Send code when modal opens (for email MFA type)
  useEffect(() => {
    if (isOpen && mfaType === 'email' && !hasSentCode.current) {
      hasSentCode.current = true;
      sendCode();
    }
    if (isOpen && mfaType === 'mobile' && !hasSentCode.current) {
      hasSentCode.current = true;
      sendCode();
    }
  }, [isOpen, mfaType]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCode('');
      setError('');
      setResendTimer(0);
      hasSentCode.current = false;
    }
  }, [isOpen]);

  // Timer for resend cooldown
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const sendCode = async () => {
    setLoading(true);
    setError('');
    try {
      await mfaService.setupEmailMFA(type);
      setResendTimer(60);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de l\'envoi du code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!code || code.length !== 6) {
      setError('Veuillez entrer un code à 6 chiffres');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await mfaService.disableMFA(code, "email", userType ?? 'individual');
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Code invalide');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0 || loading) return;
    await sendCode();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Désactiver l'authentification à deux facteurs" infoModal={true}>
      <div className="mfa-disable-modal">
        <div className="alert-warning">
          <div className="alert-content">
            <strong>Attention :</strong>
            <p>Désactiver la double authentification rendra votre compte moins sécurisé.</p>
          </div>
        </div>

        <div className="verification-section">
          <p className="verification-text">
            {mfaType === 'email'
              ? 'Un code de vérification a été envoyé à votre adresse email. Veuillez le saisir ci-dessous :'
              : 'Entrez votre code d\'authentification pour confirmer la désactivation :'}
          </p>

          <CodeInput
            value={code}
            onChange={setCode}
            length={6}
            error={error}
            disabled={loading}
            autoFocus={true}
            onComplete={(completedCode) => {
              console.log('Code completed:', completedCode);
            }}
          />

          {error && <div className="error-message">{error}</div>}

          {mfaType === 'email' && (
            <div className="resend-section">
              <button
                className="resend-link"
                onClick={handleResendCode}
                disabled={resendTimer > 0 || loading}
              >
                {resendTimer > 0
                  ? `Renvoyer le code (${resendTimer}s)`
                  : 'Renvoyer le code'}
              </button>
            </div>
          )}
        </div>

        <div className="modal-actions">
          <Button
            type={Button_Type.SECONDARY}
            style={Button_Style.OUTLINED}
            label="Annuler"
            onClick={onClose}
          />
          <Button
            type={Button_Type.Error}
            style={Button_Style.SOLID}
            label="Désactiver"
            onClick={handleDisable}
            disabled={loading || code.length !== 6}
          />
        </div>
      </div>
    </Modal>
  );
};

export default MFADisableModal;